import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "../../components/modal/Modal";
import SignatureCanvas from "react-signature-canvas";
import { Trash2, FileText, Eye } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useS3Upload, S3TempUrl } from "../../utils/s3Client";
import {
  ChecklistItem,
  Section,
  Question,
  ActionReport,
} from "../../types/api";
import { usePerformAudits } from "../../store/store";
import { isDefined } from "../../common/utils";

const PerformCheckList = () => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { currentAudit, clearCurrentAudit, updateAudit, createPerformAudit } =
    usePerformAudits();
  const [formData, setFormData] = useState<{
    auditId: string;
    questions: {
      [key: string]: {
        question: string;
        isSelected: string;
        risk: string;
        comments: string;
      };
    };
    actionReport: ActionReport;
  }>({
    auditId: "",
    questions: {},
    actionReport: {
      observation: "",
      uploadFiles: [],
      actionPlan: "",
      severity: "",
      assignedTo: "",
      assignedAuditors: "",
      targetCompletionDate: "",
      actualCompletionDate: "",
      status: "",
      closureEvidence: [],
      remarks: "",
      signature: "",
    },
  });

  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const { audit, isView } = useLocation().state;

  const navigate = useNavigate();
  const { getsignedurl } = S3TempUrl();

  const handleClose = () => {
    setIsModalOpen(false);
    clearCurrentAudit();
    navigate("/perform-audits");
  };

  const handleFormChange = (
    checklistIndex: number,
    sectionIndex: number,
    questionIndex: number,
    field: "question" | "isSelected" | "risk" | "comments",
    value: string
  ) => {
    const key = `question_${checklistIndex}_${sectionIndex}_${questionIndex}`;
    setFormData((prev) => ({
      ...prev,
      questions: {
        ...prev.questions,
        [key]: {
          ...prev.questions[key],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    // Validate that status is selected
    if (!formData.actionReport.status || formData.actionReport.status === "") {
      alert("Please select a status before submitting");
      return;
    }

    let signatureData = "";
    if (sigCanvasRef.current) {
      signatureData = sigCanvasRef.current.toDataURL("image/png");
    }

    if (!signatureData || sigCanvasRef.current?.isEmpty()) {
      alert("Please provide a signature before submitting");
      return;
    }

    const submissionData = {
      ...formData,
      actionReport: {
        ...formData.actionReport,
        signature: signatureData,
      },
    };
    if (
      audit?.audit_status === "Scheduled" &&
      formData.actionReport.status === "In Progress"
    ) {
      createPerformAudit(submissionData);
    } else if (
      audit?.audit_status === "Scheduled" &&
      formData.actionReport.status === "Completed"
    ) {
      createPerformAudit(submissionData);
    } else if (
      audit?.audit_status === "In Progress" &&
      formData.actionReport.status === "In Progress"
    ) {
      updateAudit(audit?._id || "", submissionData as any);
    } else if (
      audit?.audit_status === "In Progress" &&
      formData.actionReport.status === "Completed"
    ) {
      updateAudit(audit?._id || "", submissionData as any);
    }
    handleClose();
  };

  const clearSignatureCanvas = (): void => {
    sigCanvasRef.current?.clear();
  };

  // S3 upload functionality
  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  // File upload handlers
  const onDropUploadFiles = async (acceptedFiles: File[]) => {
    try {
      const uploadedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const url = await uploadFileToS3(file);
          return {
            name: file.name,
            url,
            type: file.type,
            size: file.size,
          };
        })
      );

      setFormData((prev) => ({
        ...prev,
        actionReport: {
          ...prev.actionReport,
          uploadFiles: [...prev.actionReport.uploadFiles, ...uploadedFiles],
        },
      }));
    } catch (err) {
      console.error("File upload error:", err);
    }
  };

  const onDropClosureEvidence = async (acceptedFiles: File[]) => {
    try {
      const uploadedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const url = await uploadFileToS3(file);
          return {
            name: file.name,
            url,
            type: file.type,
            size: file.size,
          };
        })
      );

      setFormData((prev) => ({
        ...prev,
        actionReport: {
          ...prev.actionReport,
          closureEvidence: [
            ...prev.actionReport.closureEvidence,
            ...uploadedFiles,
          ],
        },
      }));
    } catch (err) {
      console.error("File upload error:", err);
    }
  };

  const {
    getRootProps: getUploadFilesProps,
    getInputProps: getUploadFilesInputProps,
  } = useDropzone({
    onDrop: onDropUploadFiles,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg"],
      "image/png": [".png"],
    },
    maxSize: 5 * 1024 * 1024,
  });

  const {
    getRootProps: getClosureEvidenceProps,
    getInputProps: getClosureEvidenceInputProps,
  } = useDropzone({
    onDrop: onDropClosureEvidence,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg"],
      "image/png": [".png"],
    },
    maxSize: 5 * 1024 * 1024,
  });

  const handleRemoveUploadFile = (index: number) => {
    const newFiles = [...formData.actionReport.uploadFiles];
    newFiles.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      actionReport: {
        ...prev.actionReport,

        uploadFiles: newFiles,
      },
    }));
  };

  const handleViewFile = async (file: { name: string; type: string }) => {
    try {
      const fileurl = await getsignedurl(file.name, file.type);
      window.open(fileurl, "_blank");
    } catch (err) {
      console.error("Error viewing file:", err);
    }
  };

  const handleRemoveClosureEvidence = (index: number) => {
    const newFiles = [...formData.actionReport.closureEvidence];
    newFiles.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      actionReport: {
        ...prev.actionReport,
        closureEvidence: newFiles,
      },
    }));
  };

  const handleActionReportChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      actionReport: {
        ...prev.actionReport,
        [name]: value,
      },
    }));
  };
  useEffect(() => {
    if (audit?.checklist) {
      const initialQuestions: {
        [key: string]: {
          question: string;
          isSelected: string;
          risk: string;
          comments: string;
        };
      } = {};

      audit.checklist.forEach(
        (checklistItem: ChecklistItem, checklistIndex: number) => {
          checklistItem.sections?.forEach(
            (section: Section, sectionIndex: number) => {
              section.questions?.forEach(
                (question: Question, questionIndex: number) => {
                  const key = `question_${checklistIndex}_${sectionIndex}_${questionIndex}`;
                  initialQuestions[key] = {
                    question: question.text,
                    isSelected: "",
                    risk: "",
                    comments: "",
                  };
                }
              );
            }
          );
        }
      );

      if (
        (isView && currentAudit) ||
        (currentAudit?.actionReport.status === "In Progress" &&
          isView === false)
      ) {
        setFormData((prev) => ({
          ...prev,
          auditId: currentAudit.auditId || audit?._id || "",
          questions: currentAudit.questions || initialQuestions,
          actionReport: {
            ...prev.actionReport,
            observation: currentAudit.actionReport?.observation || "",
            uploadFiles: currentAudit.actionReport?.uploadFiles || [],
            actionPlan: currentAudit.actionReport?.actionPlan || "",
            severity: currentAudit.actionReport?.severity || "",
            assignedTo: currentAudit.actionReport?.assignedTo || "",
            assignedAuditors:
              currentAudit.actionReport?.assignedAuditors ||
              audit?.assignedAuditors ||
              "",
            targetCompletionDate:
              currentAudit.actionReport?.targetCompletionDate || "",
            actualCompletionDate:
              currentAudit.actionReport?.actualCompletionDate || "",
            status: currentAudit.actionReport?.status || "",
            closureEvidence: currentAudit.actionReport?.closureEvidence || [],
            remarks: currentAudit.actionReport?.remarks || "",
            signature: currentAudit.actionReport?.signature || "",
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          auditId: audit?._id || "",
          questions: initialQuestions,
          actionReport: {
            ...prev.actionReport,
            assignedAuditors: audit?.assignedAuditors || "",
          },
        }));
      }
    }
  }, [audit, isView, currentAudit]);

  // Load signature data into canvas when viewing existing audit
  useEffect(() => {
    if (
      currentAudit &&
      isDefined(currentAudit.actionReport?.signature) &&
      sigCanvasRef.current !== null
    ) {
      sigCanvasRef.current.fromDataURL(currentAudit.actionReport.signature, {
        width: 300,
        height: 125,
      });
    }
  }, [isView, currentAudit]);

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      modalClass="bg-white rounded-lg w-full h-full max-w-none max-h-none overflow-hidden"
      closeButton={false}
    >
      <div className="shadow-lg rounded-lg w-full h-full bg-[#F8F8FA] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-3 py-4 mt-2 bg-[#F8F8FA] rounded-t-lg">
          <h1 className="font-bold text-xl text-[#1b428a]">
            Perform Check List
          </h1>
          {/* Audit Info */}
          <div className="grid grid-cols-2  sm:grid-cols-3  md:grid-cols-5 pl-6 gap-4 text-sm">
            <div>
              <p className=" text-black font-semibold">Audit ID</p>
              <p className="font-semibold text-[#4876b6]">{audit?._id || ""}</p>
            </div>
            <div>
              <p className=" text-black font-semibold">Audit Date</p>
              <p className="font-semibold text-[#4876b6]">
                {audit?.startDate
                  ? new Date(audit.startDate).toLocaleDateString()
                  : ""}
              </p>
            </div>
            <div>
              <p className=" text-black font-semibold">Premise Name / ID</p>
              <p className="font-semibold text-[#4876b6]">
                {audit?.premise || ""}
              </p>
            </div>
            <div>
              <p className=" text-black font-semibold">Auditor Name(s)</p>
              <p className="font-semibold text-[#4876b6]">
                {audit?.assignedAuditors || ""}
              </p>
            </div>

            <div>
              <p className=" text-black font-semibold">Audit Status</p>
              <p
                className={`font-semibold ${
                  audit?.audit_status === "Completed"
                    ? "text-green-500"
                    : audit?.audit_status === "In Progress"
                    ? "text-orange-500"
                    : "text-red-500"
                }`}
              >
                {audit?.audit_status || ""}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <form onSubmit={handleSubmit} id="performCheckListForm">
            {audit?.checklist?.map(
              (checklistItem: ChecklistItem, checklistIndex: number) =>
                checklistItem.sections?.map(
                  (section: Section, sectionIndex: number) => (
                    <div
                      key={`${checklistItem._id}-${section.id}`}
                      className="bg-white rounded-lg border mb-6"
                    >
                      <div className="bg-[#e4e8ee] px-4 py-3 font-semibold text-[#0e0e0e] text-lg">
                        {sectionIndex + 1}) {checklistItem.checkList}
                      </div>
                      <div className="p-3">
                        <div className="grid grid-cols-12 items-center bg-[#e3f2fd] text-sm font-semibold text-black px-4 py-3 border-b">
                          <div className="col-span-5">{section.title}</div>
                          <div className="col-span-2 flex justify-around">
                            <span>Yes</span>
                            <span>No</span>
                            <span>N/A</span>
                          </div>
                          <div className="col-span-2 ml-4">Risk</div>
                          <div className="col-span-3">Comments</div>
                        </div>

                        <div className="divide-y">
                          {section.questions?.map(
                            (question: Question, questionIndex: number) => (
                              <div
                                key={question.id}
                                className="grid grid-cols-12 items-center px-4 py-3 gap-2 text-sm"
                              >
                                <div className="col-span-5">
                                  {question.text}
                                </div>
                                <div className="col-span-2 flex justify-around">
                                  <input
                                    type="radio"
                                    name={`question-${checklistIndex}-${sectionIndex}-${questionIndex}`}
                                    value="yes"
                                    checked={
                                      formData.questions[
                                        `question_${checklistIndex}_${sectionIndex}_${questionIndex}`
                                      ]?.isSelected === "yes"
                                    }
                                    onChange={(e) =>
                                      handleFormChange(
                                        checklistIndex,
                                        sectionIndex,
                                        questionIndex,
                                        "isSelected",
                                        e.target.value
                                      )
                                    }
                                    disabled={isView}
                                    className="form-radio text-blue-600"
                                  />
                                  <input
                                    type="radio"
                                    name={`question-${checklistIndex}-${sectionIndex}-${questionIndex}`}
                                    value="no"
                                    checked={
                                      formData.questions[
                                        `question_${checklistIndex}_${sectionIndex}_${questionIndex}`
                                      ]?.isSelected === "no"
                                    }
                                    onChange={(e) =>
                                      handleFormChange(
                                        checklistIndex,
                                        sectionIndex,
                                        questionIndex,
                                        "isSelected",
                                        e.target.value
                                      )
                                    }
                                    disabled={isView}
                                    className="form-radio text-blue-600"
                                  />
                                  <input
                                    type="radio"
                                    name={`question-${checklistIndex}-${sectionIndex}-${questionIndex}`}
                                    value="na"
                                    checked={
                                      formData.questions[
                                        `question_${checklistIndex}_${sectionIndex}_${questionIndex}`
                                      ]?.isSelected === "na"
                                    }
                                    onChange={(e) =>
                                      handleFormChange(
                                        checklistIndex,
                                        sectionIndex,
                                        questionIndex,
                                        "isSelected",
                                        e.target.value
                                      )
                                    }
                                    disabled={isView}
                                    className="form-radio text-blue-600 w-3 h-3"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <select
                                    name={`question-${checklistIndex}-${sectionIndex}-${questionIndex}-risk`}
                                    className="border rounded px-2 py-1 w-full"
                                    value={
                                      formData.questions[
                                        `question_${checklistIndex}_${sectionIndex}_${questionIndex}`
                                      ]?.risk || ""
                                    }
                                    onChange={(e) =>
                                      handleFormChange(
                                        checklistIndex,
                                        sectionIndex,
                                        questionIndex,
                                        "risk",
                                        e.target.value
                                      )
                                    }
                                    disabled={isView}
                                  >
                                    <option value="">Select</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                  </select>
                                </div>
                                <div className="col-span-3">
                                  <textarea
                                    name={`question-${checklistIndex}-${sectionIndex}-${questionIndex}-comments`}
                                    className="border rounded px-2 py-1 w-full"
                                    placeholder="Add comments..."
                                    value={
                                      formData.questions[
                                        `question_${checklistIndex}_${sectionIndex}_${questionIndex}`
                                      ]?.comments || ""
                                    }
                                    onChange={(e) =>
                                      handleFormChange(
                                        checklistIndex,
                                        sectionIndex,
                                        questionIndex,
                                        "comments",
                                        e.target.value
                                      )
                                    }
                                    disabled={isView}
                                  />
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )
            )}

            <div className="bg-white rounded-lg border mb-6">
              <div className="bg-[#e4e8ee] px-4 py-3 font-semibold text-[#0e0e0e] text-lg">
                3) Action Report
              </div>
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-4 py-6 text-sm text-gray-700">
                  <div className=" col-start-1 col-end-2 col-span-3">
                    <label className="font-semibold mb-1">Observation</label>
                    <textarea
                      name="observation"
                      placeholder="Enter here"
                      value={formData.actionReport.observation}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border w-full rounded px-2 py-2 h-20"
                    />
                  </div>
                  <div className="col-start-2 col-span-2">
                    <label className="font-semibold mb-1">
                      Upload Files{" "}
                      <span className="text-xs text-gray-500">
                        (Supported file formats:{" "}
                        <span className="text-orange-500">
                          DOC/DOCX, PDF, TXT, JPG, PNG
                        </span>{" "}
                        Max file size:{" "}
                        <span className="text-orange-500">5 MB</span>)
                      </span>
                    </label>
                    {isView ? (
                      // View mode - show files only
                      <div className="border-2 border-gray-300 p-4 rounded-lg text-center">
                        <p className="text-gray-500 font-semibold mb-4">
                          Uploaded Files
                        </p>
                        <div className="flex gap-4 mt-4 flex-wrap">
                          {formData.actionReport.uploadFiles.map(
                            (file, index) => (
                              <div
                                key={index}
                                className="flex flex-col items-center bg-gray-100 p-3 rounded-md w-24 relative group"
                              >
                                <FileText size={32} className="text-blue-500" />
                                <p className="text-xs text-gray-600 truncate">
                                  {file.name}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewFile(file);
                                  }}
                                  className="text-xs text-blue-500 hover:underline"
                                >
                                  <Eye />
                                </button>
                              </div>
                            )
                          )}
                          {formData.actionReport.uploadFiles.length === 0 && (
                            <p className="text-gray-500 text-sm">
                              No files uploaded
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Edit mode - allow file upload
                      <div
                        className="border-2 border-gray-300 p-4 rounded-lg border-dashed text-center"
                        {...getUploadFilesProps()}
                      >
                        <input {...getUploadFilesInputProps()} />
                        <p className="text-gray-500 font-semibold">
                          Drag & Drop Your Files Here or Click
                        </p>

                        {/* Uploaded Files Preview */}
                        <div className="flex gap-4 mt-4 flex-wrap">
                          {formData.actionReport.uploadFiles.map(
                            (file, index) => (
                              <div
                                key={index}
                                className="flex flex-col items-center bg-gray-100 p-3 rounded-md w-24 relative group"
                              >
                                <FileText size={32} className="text-blue-500" />
                                <p className="text-xs text-gray-600 truncate">
                                  {file.name}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveUploadFile(index);
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                                >
                                  ×
                                </button>
                              </div>
                            )
                          )}
                          {formData.actionReport.uploadFiles.length === 0 && (
                            <p className="text-gray-500 text-sm">
                              No files selected
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col  col-end-7 col-span-3">
                    <label className="font-semibold mb-1">Action Plan</label>
                    <textarea
                      name="actionPlan"
                      placeholder="Enter here"
                      value={formData.actionReport.actionPlan}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2 h-20"
                    />
                  </div>
                </div>
                <div>
                  <div className="border border-gray-500"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-4 py-6 text-sm text-gray-700">
                  <div className="flex flex-col col-span-1">
                    <label className="font-semibold mb-1">
                      Severity / Risk Rating
                    </label>
                    <select
                      name="severity"
                      value={formData.actionReport.severity}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2"
                    >
                      <option value="">Select</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div className="flex flex-col col-span-1">
                    <label className="font-semibold mb-1">
                      Assigned To (Responsibility)
                    </label>
                    <input
                      type="text"
                      name="assignedTo"
                      placeholder="Enter Responsible person"
                      value={formData.actionReport.assignedTo}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2"
                    />
                  </div>
                  <div className="flex flex-col col-span-1">
                    <label className="font-semibold mb-1">
                      Target Completion Date
                    </label>
                    <input
                      type="date"
                      name="targetCompletionDate"
                      value={formData.actionReport.targetCompletionDate}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2"
                    />
                  </div>
                  <div className="flex flex-col col-span-1">
                    <label className="font-semibold mb-1">
                      Actual Completion Date
                    </label>
                    <input
                      type="date"
                      name="actualCompletionDate"
                      value={formData.actionReport.actualCompletionDate}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2"
                    />
                  </div>
                  <div className="flex flex-col col-span-1">
                    <label className="font-semibold mb-1">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.actionReport.status}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2"
                      required
                    >
                      <option value="Select">Select</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                  <div className="flex flex-col col-start-2 col-span-2">
                    <label className="font-semibold mb-1">
                      Closure Evidence{" "}
                      <span className="text-xs text-gray-500">
                        (Supported file formats:{" "}
                        <span className="text-orange-500">
                          DOC/DOCX, PDF, TXT, JPG, PNG
                        </span>{" "}
                        Max file size:{" "}
                        <span className="text-orange-500">5 MB</span>)
                      </span>
                    </label>
                    {isView ? (
                      // View mode - show files only
                      <div className="border-2 border-gray-300 p-4 rounded-lg text-center">
                        <p className="text-gray-500 font-semibold mb-4">
                          Closure Evidence Files
                        </p>
                        <div className="flex gap-4 mt-4 flex-wrap">
                          {formData.actionReport.closureEvidence.map(
                            (file, index) => (
                              <div
                                key={index}
                                className="flex flex-col items-center bg-gray-100 p-3 rounded-md w-24 relative group"
                              >
                                <FileText size={32} className="text-blue-500" />
                                <p className="text-xs text-gray-600 truncate">
                                  {file.name}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewFile(file);
                                  }}
                                  className="text-xs text-blue-500 hover:underline"
                                >
                                  <Eye />
                                </button>
                              </div>
                            )
                          )}
                          {formData.actionReport.closureEvidence.length ===
                            0 && (
                            <p className="text-gray-500 text-sm">
                              No files uploaded
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Edit mode - allow file upload
                      <div
                        className="border-2 border-gray-300 p-4 rounded-lg border-dashed text-center"
                        {...getClosureEvidenceProps()}
                      >
                        <input {...getClosureEvidenceInputProps()} />
                        <p className="text-gray-500 font-semibold">
                          Drag & Drop Your Files Here or Click
                        </p>

                        {/* Uploaded Files Preview */}
                        <div className="flex gap-4 mt-4 flex-wrap">
                          {formData.actionReport.closureEvidence.map(
                            (file, index) => (
                              <div
                                key={index}
                                className="flex flex-col items-center bg-gray-100 p-3 rounded-md w-24 relative group"
                              >
                                <FileText size={32} className="text-blue-500" />
                                <p className="text-xs text-gray-600 truncate">
                                  {file.name}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveClosureEvidence(index);
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                                >
                                  ×
                                </button>
                              </div>
                            )
                          )}
                          {formData.actionReport.closureEvidence.length ===
                            0 && (
                            <p className="text-gray-500 text-sm">
                              No files selected
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col col-span-1">
                    <label className="font-semibold mb-1">
                      Remarks / Notes
                    </label>
                    <textarea
                      name="remarks"
                      placeholder="Enter here"
                      value={formData.actionReport.remarks}
                      onChange={handleActionReportChange}
                      disabled={isView}
                      className="border rounded px-2 py-2 h-20"
                    />
                  </div>

                  {/* Signature Section */}
                  <div className="flex  w-1/4 flex-col col-span-4 mt-4">
                    <div className="mb-4">
                      <span className="text-black text-sm font-semibold">
                        Signature <span className="text-red-500">*</span>
                      </span>
                      {!isView && (
                        <Trash2
                          onClick={clearSignatureCanvas}
                          className="text-red-600 float-end w-5 h-5 cursor-pointer"
                        />
                      )}
                    </div>
                    <div className="w-[310px] h-[125px] border border-gray-300 rounded">
                      <SignatureCanvas
                        penColor="black"
                        canvasProps={{
                          width: 300,
                          height: 125,
                          className: "w-full h-full",
                        }}
                        ref={sigCanvasRef}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
          </form>
          <div className="flex-none py-3 px-6 bg-[#61748e] flex justify-end gap-4 rounded-bl-xl rounded-br-xl">
            <button
              type="button"
              className="bg-[#869ab6] text-white px-6 py-2 rounded-full"
              onClick={handleClose}
            >
              {isView ? "Close" : "Cancel"}
            </button>
            {!isView && (
              <button
                type="submit"
                className="bg-[#0fa9a6] text-white px-6 py-2 rounded-full"
                form="performCheckListForm"
              >
                Submit
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PerformCheckList;
